import { PageNames } from 'constants/pageNames';
import { useBrandSelector } from 'hooks/useBrandSelector';
import { usePageParam } from 'hooks/usePageParam';
import { useRenderPage } from 'hooks/useRenderPage';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchWizardData } from 'store/slices/claimContentSlice';
import { setGlobalModal } from 'store/slices/modalSlice';
import { addPage } from 'store/slices/pageListSlice';
import { Header, LeaveFlowModal } from './3-organisms';
import { OverlayModel } from './4-habitats';

export const PageMapper = () => {
	const params = useParams();

	const dispatch = useAppDispatch();

	const [currentPage, setCurrentPage] = useState<WizardPage>();

	const claimContent = useAppSelector((state) => state.claimContent);

	const { openPagesUrl } = useBrandSelector();

	const {
		loginUrl = { url: '' },
		loginIcon = { url: '' },
		loginLabelText = '',
		headerLinks = [] as Link[],
		logoUrl = { url: '' },
		logoImage = { url: '' },
	} = claimContent?.frontendConfiguration?.headerReference ?? ({} as HeaderReference);

	const { setPageParam, getPageParam } = usePageParam();

	const [displayModal, setDisplayModal] = useState(false);
	const [triggerLogout, setTriggerLogout] = useState(false);
	const [requestedUrl, setRequestedUrl] = useState('');

	const { pages: wizardPages } = claimContent.wizardData ?? {};

	const renderPage = useRenderPage(currentPage);

	const handleLogoClick = () => {
		if (!logoUrl.url?.trim()) return;
		toggleModal(logoUrl.url, true);
	};

	const navLinks = headerLinks.map((link) => ({
		action: () => toggleModal(link.url, true),
		title: link.title ?? link.text ?? 'Untitled',
	}));

	const findPageContent = (pagPath: string) => {
		return wizardPages?.find((page) => page.path === pagPath);
	};

	const isIframePage = useMemo(() => {
		return currentPage?.path?.includes(PageNames.WebSphereIframe) ?? false;
	}, [currentPage]);

	const isResultPage = useMemo(() => {
		return currentPage?.path?.includes(PageNames.ResultPage) ?? false;
	}, [currentPage]);

	const toggleModal = (link?: string, triggerLogout = false) => {
		setDisplayModal((prevState) => {
			const newState = !prevState;
			dispatch(setGlobalModal(newState));

			return newState;
		});

		setRequestedUrl(link ?? '');
		if (triggerLogout && link) {
			setTriggerLogout(true);
		}
	};

	useEffect(() => {
		if (!params?.flowName) return;
		dispatch(fetchWizardData(params.flowName));
	}, [params.flowName]);

	useEffect(() => {
		const initializePage = async () => {
			if (!claimContent.wizardData) return;

			const currentPath = getPageParam() ?? setPageParam(PageNames.StartPage);
			const pageContent = findPageContent(`/${currentPath}`);
			if (pageContent?.id) {
				setCurrentPage(pageContent);
				dispatch(addPage(pageContent));
			}
		};

		initializePage();
	}, [getPageParam(), claimContent]);

	return (
		<>
			<Header
				onLogoutClick={() => toggleModal(openPagesUrl, true)}
				navLinks={navLinks}
				logoLink={logoUrl.url ?? ''}
				logoUrl={logoImage.url ?? ''}
				loginUrl={loginUrl}
				loginIcon={loginIcon}
				loginLabelText={loginLabelText}
				onLogoClick={handleLogoClick}
			/>

			<OverlayModel hasNoBackground={isIframePage}>{renderPage}</OverlayModel>
			{displayModal && (
				<LeaveFlowModal
					setModalOpen={() => toggleModal()}
					triggerLogout={triggerLogout}
					requestedUrl={requestedUrl}
					isResultPage={isResultPage}
					//isAutoLogout={triggerAutoLogout}
				/>
			)}
		</>
	);
};

/**
 * Show logout modal if the usere have been inactive for to long
 */
// const { triggerAutoLogout } = useAutoLogoutTimer(600000); //10min show modal for autologout
// useEffect(() => {
// 	if (triggerAutoLogout && !displayModal) {
// 		toggleModel(PageNames.OpenPages);
// 	}
// }, [triggerAutoLogout]);
/******************************************************* */
